import type { VendorErrorCodeFormValues } from './types';

/** Wire payload shapes used by create/update (mirrors OpenAPI DTOs). */
export type VendorErrorCodeWritePayload = {
  manufacturerId: string;
  assetTypeId: string;
  errorCode: string;
  matchType?: string | null;
  matchKey?: string | null;
  vendorErrorCode?: string | null;
  info?: string | null;
  parameter?: string | null;
  state?: string | null;
  impactedComponent?: string | null;
  category?: string | null;
  severity?: string | null;
  firmwareVersion?: string | null;
  description?: string | null;
};

function asOptionalString(
  value: string | null | undefined,
): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return value;
}

/**
 * Create payload: omit empty optionals (do not send null).
 */
export function buildCreateRequestPayload(
  values: VendorErrorCodeFormValues,
): VendorErrorCodeWritePayload {
  const payload: VendorErrorCodeWritePayload = {
    manufacturerId: values.manufacturerId,
    assetTypeId: values.assetTypeId,
    errorCode: values.errorCode,
  };

  const matchType = asOptionalString(values.matchType);
  if (matchType !== undefined) payload.matchType = matchType;
  const matchKey = asOptionalString(values.matchKey);
  if (matchKey !== undefined) payload.matchKey = matchKey;
  const vendorErrorCode = asOptionalString(values.vendorErrorCode);
  if (vendorErrorCode !== undefined) payload.vendorErrorCode = vendorErrorCode;
  const info = asOptionalString(values.info);
  if (info !== undefined) payload.info = info;
  if (values.parameter) payload.parameter = values.parameter;
  if (values.state) payload.state = values.state;
  if (values.impactedComponent) {
    payload.impactedComponent = values.impactedComponent;
  }
  if (values.category) payload.category = values.category;
  if (values.severity) payload.severity = values.severity;
  const firmwareVersion = asOptionalString(values.firmwareVersion);
  if (firmwareVersion !== undefined) payload.firmwareVersion = firmwareVersion;
  const description = asOptionalString(values.description);
  if (description !== undefined) payload.description = description;

  return payload;
}

/**
 * Update payload: Ant Design allowClear → explicit null so Prisma clears the column.
 * Required identity fields are always sent from the form.
 */
export function buildUpdateRequestPayload(
  values: VendorErrorCodeFormValues,
): VendorErrorCodeWritePayload {
  return {
    manufacturerId: values.manufacturerId,
    assetTypeId: values.assetTypeId,
    errorCode: values.errorCode,
    matchType: asOptionalString(values.matchType) ?? null,
    matchKey: asOptionalString(values.matchKey) ?? null,
    vendorErrorCode: asOptionalString(values.vendorErrorCode) ?? null,
    info: asOptionalString(values.info) ?? null,
    parameter: values.parameter ? values.parameter : null,
    state: values.state ? values.state : null,
    impactedComponent: values.impactedComponent
      ? values.impactedComponent
      : null,
    category: values.category ? values.category : null,
    severity: values.severity ? values.severity : null,
    firmwareVersion: asOptionalString(values.firmwareVersion) ?? null,
    description: asOptionalString(values.description) ?? null,
  };
}

/**
 * Detects HTTP 409 from VendorErrorCodeApiError without importing the client.
 */
export function isConflictError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === 'VendorErrorCodeApiError' &&
    'status' in error &&
    (error as { status?: number }).status === 409
  );
}
